import { FunctionComponent, useState } from 'react'

import styled from 'styled-components'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm, useWatch } from 'react-hook-form'

import { CTAButton } from 'src/Components/Buttons/CTAButton'
import { PrimaryAlert } from 'src/Components/PrimaryAlert'
import { changeEmail } from 'src/Redux/auth-process/credentialsSlice/api-actions'
import { useAppDispatch } from 'src/Hooks/redux'
import { PrimaryInput } from 'src/Components/PrimaryInput'

import { FormDataProps } from './ChangeEmailFormProps'
import { schema } from './helpers/schema'

import { ChangeEmailFormContainer } from './styles'
const StyledCTAButton = styled(CTAButton)`
  width: fit-content;
  margin: 0 auto;
  padding-left: 40px;
  padding-right: 40px;
`

export const ChangeEmailForm: FunctionComponent = () => {
  const dispatch = useAppDispatch()

  const [invalid, setInvalid] = useState('')
  const [isEmailUsed, setEmailUsed] = useState(false)
  const [isEmailChanged, setEmailChanged] = useState(false)

  const {
    control,
    getValues,
    clearErrors,
    formState: { errors },
  } = useForm<FormDataProps>({
    criteriaMode: 'firstError',
    reValidateMode: 'onChange',
    mode: 'onBlur',
    resolver: yupResolver(schema),
  })

  const values = getValues()
  const watchChangeEmail = useWatch({
    control,
    name: 'changeEmail',
    defaultValue: '',
  })

  const handleClearErrors = (): void => {
    clearErrors()
    setInvalid('')
    setEmailUsed(false)
  }

  const handleChangeEmail = (): void => {
    if (errors && errors.changeEmail?.message?.length) {
      setInvalid('Invalid email address')
      return
    }
    void dispatch(changeEmail(values.changeEmail))
      .unwrap()
      .then((res) => {
        setEmailUsed(false)
        setEmailChanged(true)
        values.changeEmail = ''
        return res
      })
      .catch(() => {
        setEmailUsed(true)
        setEmailChanged(false)
        values.changeEmail = ''
      })
  }

  return (
    <>
      {isEmailChanged && (
        <PrimaryAlert type='success' hasCross={false}>
          Your email has been changed. Please check your email and click on the
          provided link to confirm your new email address.
        </PrimaryAlert>
      )}
      {isEmailUsed && (
        <PrimaryAlert type='danger' title='Error' hasCross={false}>
          The email address is already in use by another account
        </PrimaryAlert>
      )}

      <ChangeEmailFormContainer>
        <Controller
          name='changeEmail'
          control={control}
          defaultValue=''
          render={({ field }) => (
            <PrimaryInput
              {...field}
              type='email'
              placeholder='Enter new email'
              error={invalid || null}
              onFocus={handleClearErrors}
            />
          )}
        />

        <StyledCTAButton
          type='button'
          disabled={
            !watchChangeEmail?.length || invalid.length > 0 || isEmailUsed
          }
          onClick={handleChangeEmail}
        >
          Update email
        </StyledCTAButton>
      </ChangeEmailFormContainer>
    </>
  )
}
